const getExtname = (value = "") => {
  const cleanValue = value.split("?")[0];
  const lastDotIndex = cleanValue.lastIndexOf(".");
  const lastSlashIndex = cleanValue.lastIndexOf("/");

  if (lastDotIndex < 0 || lastDotIndex < lastSlashIndex) {
    return null;
  }

  return cleanValue.slice(lastDotIndex) || null;
};

const getMediaAttributes = (media) => {
  if (!media) {
    return null;
  }

  const ext = media.ext || getExtname(media.url || "") || null;

  return {
    ext,
    url: media.url || null,
    localFile: media.localFile || null,
  };
};

const makeMedia = (media) => ({
  data: media ? { attributes: getMediaAttributes(media) } : null,
});

const makeAuthor = (author) => {
  if (!author) {
    return undefined;
  }

  return {
    data: {
      id: author.id || author.slug,
      attributes: {
        name: author.name,
        slug: author.slug,
        title: author.title || "",
        avatar: makeMedia(author.avatar),
      },
    },
  };
};

const getPublishTimestamp = (post) => {
  const publishValue =
    post?.publishAt || post?.attributes?.publishAt || post?.date || post?.attributes?.date;

  if (!publishValue) {
    return null;
  }

  const timestamp = new Date(publishValue).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const isPublishTimeReached = (post, now = new Date()) => {
  const publishTimestamp = getPublishTimestamp(post);

  if (publishTimestamp === null) {
    return true;
  }

  return publishTimestamp <= now.getTime();
};

const normalizeStaticBlogPost = (post) => ({
  id: post.id || post.slug,
  attributes: {
    title: post.title,
    slug: post.slug,
    date: post.date,
    publishAt: post.publishAt,
    content: post.content,
    description: post.description,
    featuredPost: Boolean(post.featuredPost),
    featuredImage: makeMedia(post.featuredImage),
    category: {
      data: {
        id: post.category.id || post.category.slug,
        attributes: {
          name: post.category.name,
          slug: post.category.slug,
        },
      },
    },
    author: makeAuthor(post.author),
  },
});

const normalizeStaticBlogPosts = (posts = []) =>
  posts.map(normalizeStaticBlogPost);

const filterPublishedPosts = (posts = [], now = new Date()) =>
  posts.filter((post) => isPublishTimeReached(post, now));

const sortPostsByDateDesc = (posts = []) =>
  [...posts].sort((left, right) => {
    const leftDate = getPublishTimestamp(left) || 0;
    const rightDate = getPublishTimestamp(right) || 0;
    return rightDate - leftDate;
  });

const mergeBlogPosts = (strapiPosts = [], staticPosts = []) => {
  const postsBySlug = new Map();

  strapiPosts.forEach((post) => {
    const slug = post?.attributes?.slug;
    if (slug) {
      postsBySlug.set(slug, post);
    }
  });

  staticPosts.forEach((post) => {
    const slug = post?.attributes?.slug;
    if (slug) {
      postsBySlug.set(slug, post);
    }
  });

  return sortPostsByDateDesc(Array.from(postsBySlug.values()));
};

const getBlogCategories = (posts = []) =>
  posts.reduce((categories, post) => {
    const category = post?.attributes?.category?.data;

    if (
      category &&
      categories.findIndex((item) => item.id === category.id) < 0
    ) {
      categories.push({
        ...category,
        pagePath: `/blog/category/${category.attributes.slug}`,
      });
    }

    return categories;
  }, []);

module.exports = {
  filterPublishedPosts,
  getBlogCategories,
  getPublishTimestamp,
  isPublishTimeReached,
  mergeBlogPosts,
  normalizeStaticBlogPost,
  normalizeStaticBlogPosts,
  sortPostsByDateDesc,
};
